import React, {useState} from 'react';
import HelpTooltip from '../HelpTooltip';
import {checkVat as requestVatCheck} from './api';

// USt-ID-Prüfung gegen den EU-VIES-Dienst (Original `checkVat`,
// `renderVatResult`, `vatWhen`, Zeilen 2138–2204). weclapp bietet die Prüfung
// nicht über seine Schnittstelle an, zapft intern aber denselben Dienst an.
// Das Ergebnis wird je USt-ID zwischengespeichert, damit es ein erneutes
// Zeichnen des Beleg-Kopfes übersteht.

/** Vergleichsform: Großbuchstaben, nur Buchstaben und Ziffern. */
function vatNorm(s) {
    return String(s || '').toUpperCase().replace(/[^A-Z0-9ÄÖÜ]/g, '');
}

/** Zeitpunkt und Beleg-Nummer der Abfrage. */
function VatWhen({result}) {
    if (!result.requestDate) {
        return null;
    }

    let d = result.requestDate;
    try {
        const dt = new Date(result.requestDate);
        if (!isNaN(dt)) {
            d = dt.toLocaleDateString('de-DE') + ' ' + dt.toLocaleTimeString('de-DE', {hour: '2-digit', minute: '2-digit'});
        }
    } catch (e) {
        // Unlesbares Datum unverändert anzeigen.
    }

    return (
        <div className="vat-when">
            Geprüft am {d} über EU-VIES
            {result.requestIdentifier ? ` · Beleg-Nr. ${result.requestIdentifier}` : ''}
        </div>
    );
}

/** Darstellung des Prüfergebnisses samt Abgleich gegen die Belegadresse. */
function VatResult({result, order}) {
    if (!result) {
        return null;
    }

    if (result.failed) {
        return (
            <div className="vatres">
                <div className="vat-head vat-unchecked"><span className="vat-dot"/>Prüfung fehlgeschlagen</div>
            </div>
        );
    }

    if (result.status === 'unchecked') {
        return (
            <div className="vatres">
                <div className="vat-head vat-unchecked"><span className="vat-dot"/>Nicht geprüft</div>
                <div className="vat-detail">{result.message || 'VIES-Dienst nicht verfügbar'}</div>
            </div>
        );
    }

    if (result.status === 'invalid') {
        return (
            <div className="vatres">
                <div className="vat-head vat-invalid"><span className="vat-dot"/>Ungültig</div>
                <div className="vat-detail">Der VIES-Dienst kennt diese USt-ID nicht.</div>
                <VatWhen result={result}/>
            </div>
        );
    }

    // Gültig: registrierte Daten gegen die Belegadresse des Auftrags halten.
    const a = (order && order.recordAddress) || {};
    const ordName = a.company || (order && order.customerName) || '';
    const ordCity = a.city || '';
    let cmp = null;

    if (result.name || result.address) {
        const regName = vatNorm(result.name);
        const regAddr = vatNorm(result.address);
        // „Stimmt überein" heißt: der Belegwert steckt (normalisiert) im
        // Registerwert — oder umgekehrt.
        const nameOk = !ordName || !regName || regName.includes(vatNorm(ordName)) || vatNorm(ordName).includes(regName);
        const cityOk = !ordCity || !regAddr || regAddr.includes(vatNorm(ordCity));
        const both = nameOk && cityOk;
        const mark = (ok) => <span className={`vat-match ${ok ? 'ok' : 'no'}`}>{ok ? '✓' : '≠'}</span>;

        cmp = (
            <div className="vat-detail">
                <div>{mark(nameOk)} Name: <b>{result.name || '—'}</b></div>
                <div>{mark(cityOk)} Adresse: {result.address ? result.address.replace(/\n/g, ', ') : '—'}</div>
                <div style={{marginTop: 3}}>
                    {both
                        ? <span className="vat-match ok">stimmt mit der Belegadresse überein</span>
                        : <>
                            <span className="vat-match no">weicht von der Belegadresse ab</span>{' '}
                            <span className="muted">(Beleg: {[ordName, ordCity].filter(Boolean).join(', ') || '—'})</span>
                        </>}
                </div>
            </div>
        );
    }

    return (
        <div className="vatres">
            <div className="vat-head vat-valid"><span className="vat-dot"/>Gültig</div>
            {cmp}
            <VatWhen result={result}/>
        </div>
    );
}

/**
 * Zeile „USt-ID" in der Kunden-Kachel: Nummer, Knopf „prüfen" und das
 * zwischengespeicherte Ergebnis.
 *
 * @param {string} vatId
 * @param {object} order - Für den Abgleich gegen die Belegadresse.
 * @param {object} cache - Bisherige Ergebnisse, Schlüssel ist die USt-ID.
 * @param {(vat: string, result: object) => void} onResult
 */
export default function VatCheckField({vatId, order, cache, onResult}) {
    const [busy, setBusy] = useState(false);
    const result = cache[vatId];

    if (!vatId) {
        return <span className="muted">—</span>;
    }

    const run = async () => {
        setBusy(true);
        try {
            const data = await requestVatCheck(vatId);
            onResult(vatId, data);
        } catch (e) {
            onResult(vatId, {failed: true});
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            {vatId}
            <button type="button" className="vatbtn" disabled={busy} onClick={run}>
                {busy ? 'prüfe…' : (result ? 'erneut prüfen' : 'prüfen')}
            </button>
            <HelpTooltip id="oe-vat-help" placement="top">
                Fragt die USt-ID beim EU-Dienst VIES ab — dieselbe Quelle, die weclapp intern benutzt.
                Das Ergebnis wird mit der Belegadresse verglichen und bis zum Verlassen der Seite behalten.
            </HelpTooltip>
            <VatResult result={result} order={order}/>
        </>
    );
}
